/**
 * AgentsPage — D-1. 에이전트 전체보기
 *
 * 섹션 구성 (상세기능정의서 D-1 / 화면기획서 studio-agent-all-web.html 기준):
 *   1) 구독 중인 에이전트 — init subscriptions 데이터, [열기] 이동, [사용가이드] 모달
 *   2) 출시 예정 에이전트 — GET /indipia/agents/upcoming, [알림 받기] → POST /notify
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@indipia/shared';
import { Badge, Button, Card, Modal, Skeleton } from '@indipia/ui-core';
import { fetchUpcomingAgents, subscribeAgentNotify, fetchAgentGuide } from '../api/agents';
import { AGENT_MENU_CONFIG } from '../../registry/menu-config';

// ── 에이전트 시각 매핑 ────────────────────────────────────────────────────────
// group: D-08 §6.4 groupTheme 스코프 ('01'=사진, '02'=현황, '03'=스케줄)

const AGENT_VISUAL: Record<number, { group: string; icon: React.ReactNode }> = {
  1: {
    group: '01',
    icon: (
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
        <circle cx="12" cy="13" r="4"/>
      </svg>
    ),
  },
  2: {
    group: '03',
    icon: (
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
        <line x1="8" y1="14" x2="8.01" y2="14" strokeWidth="2.5"/><line x1="12" y1="14" x2="12.01" y2="14" strokeWidth="2.5"/>
        <line x1="16" y1="14" x2="16.01" y2="14" strokeWidth="2.5"/><line x1="8" y1="18" x2="8.01" y2="18" strokeWidth="2.5"/>
        <line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5"/>
      </svg>
    ),
  },
  8: {
    group: '02',
    icon: (
      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
        <line x1="3" y1="20" x2="21" y2="20"/>
      </svg>
    ),
  },
};

// ── 사용 가이드 모달 ────────────────────────────────────────────────────────────

function GuideModal({ html, onClose }: { html: string; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title="사용 가이드">
      <iframe
        srcDoc={html}
        className="h-96 w-full border-none"
        sandbox="allow-same-origin allow-scripts"
        title="사용 가이드"
      />
    </Modal>
  );
}

// ── 메인 페이지 ────────────────────────────────────────────────────────────────

export function AgentsPage() {
  const subscriptions = useAppStore((s) => s.subscriptions);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: upcoming = [], isLoading: upcomingLoading } = useQuery({
    queryKey: ['agents', 'upcoming'],
    queryFn: fetchUpcomingAgents,
  });

  const [guideHtml, setGuideHtml] = useState<string | null>(null);
  const [guideLoading, setGuideLoading] = useState(false);

  async function openGuide(agentId: number) {
    if (guideLoading) return;
    setGuideLoading(true);
    try {
      const html = await fetchAgentGuide(agentId);
      setGuideHtml(html);
    } catch {
      // 가이드 없으면 무시
    } finally {
      setGuideLoading(false);
    }
  }

  const notifyMutation = useMutation({
    mutationFn: subscribeAgentNotify,
    onSuccess: (data) => {
      queryClient.setQueryData(
        ['agents', 'upcoming'],
        (prev: Awaited<ReturnType<typeof fetchUpcomingAgents>> | undefined) =>
          prev?.map((a) =>
            a.agentId === data.agentId ? { ...a, notifySubscribed: true } : a,
          ),
      );
    },
  });

  const menuMap = new Map(AGENT_MENU_CONFIG.map((c) => [c.numericAgentId, c]));
  // 정렬은 API agentOrder(TM_AGENT.AGENT_ORDER) 우선, 없으면 정적 config order로 폴백
  const sortedSubscriptions = [...subscriptions].sort(
    (a, b) =>
      (a.agentOrder ?? menuMap.get(a.agentId)?.order ?? 999) -
      (b.agentOrder ?? menuMap.get(b.agentId)?.order ?? 999),
  );

  return (
    <div className="mx-auto max-w-3xl space-y-12 p-8">

      {/* ── 1. 구독 중인 에이전트 ──────────────────────────────────────── */}
      <section>
        <div className="mb-6">
          <p className="mb-1.5 text-caption font-bold uppercase tracking-widest text-group">
            구독 중인 에이전트
          </p>
          <h2 className="text-title-sm font-extrabold tracking-tight text-text">내 스튜디오 에이전트</h2>
          <p className="mt-1 text-xs text-text-muted">
            현재 {sortedSubscriptions.length}개 AI 에이전트가 활성화되어 있습니다
          </p>
        </div>

        <div className="space-y-3">
          {sortedSubscriptions.length === 0 && (
            <p className="py-8 text-center text-sm text-text-muted">구독 중인 에이전트가 없습니다</p>
          )}
          {sortedSubscriptions.map((sub) => {
            const visual = AGENT_VISUAL[sub.agentId];
            const menuConf = menuMap.get(sub.agentId);
            return (
              <Card
                key={sub.agentSubId}
                data-group={visual?.group}
                className="transition-colors hover:border-group"
              >
                <div className="flex items-center gap-5">
                  {/* 아이콘 패널 */}
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md bg-group-subtle text-group">
                    {visual?.icon ?? (
                      <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" rx="1.5"/>
                        <rect x="14" y="3" width="7" height="7" rx="1.5"/>
                        <rect x="3" y="14" width="7" height="7" rx="1.5"/>
                        <rect x="14" y="14" width="7" height="7" rx="1.5"/>
                      </svg>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-heading-sm font-extrabold tracking-tight text-text">{sub.agentName}</p>
                    {sub.recommendTitle && (
                      <p className="mt-1 text-xs leading-relaxed text-text-muted">{sub.recommendTitle}</p>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <Badge status="success">구독중</Badge>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => menuConf && navigate(menuConf.defaultPath)}
                      disabled={!menuConf}
                    >
                      열기
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => openGuide(sub.agentId)}
                      disabled={guideLoading}
                    >
                      사용가이드 →
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ── 2. 출시 예정 에이전트 ─────────────────────────────────────── */}
      <section>
        <div className="mb-6">
          <p className="mb-1.5 text-caption font-bold uppercase tracking-widest text-group">
            출시 예정 에이전트
          </p>
          <h2 className="text-title-sm font-extrabold tracking-tight text-text">곧 만나볼 수 있어요</h2>
          <p className="mt-1 text-xs text-text-muted">
            더 많은 AI 에이전트가 준비 중입니다. 알림을 받고 가장 먼저 사용해 보세요
          </p>
        </div>

        <div className="space-y-2.5">
          {upcomingLoading &&
            [0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-16 rounded-md" />
            ))}
          {!upcomingLoading && upcoming.length === 0 && (
            <p className="py-8 text-center text-sm text-text-muted">출시 예정 에이전트가 없습니다</p>
          )}
          {upcoming.map((agent) => (
            <Card key={agent.agentId}>
              <div className="flex items-center gap-4">
                {/* 아이콘 패널 */}
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-surface-muted">
                  <svg
                    width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                    className="text-text-muted"
                  >
                    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                    <polyline points="2 17 12 22 22 17"/>
                    <polyline points="2 12 12 17 22 12"/>
                  </svg>
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-body-md font-bold tracking-tight text-text-secondary">{agent.agentName}</p>
                  <p className="mt-1 text-xs leading-relaxed text-text-muted">{agent.agentDescription}</p>
                </div>

                <div className="flex shrink-0 flex-col items-end gap-2">
                  <Badge variant="outline">출시예정</Badge>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={notifyMutation.isPending && notifyMutation.variables === agent.agentId}
                    disabled={agent.notifySubscribed}
                    onClick={() => !agent.notifySubscribed && notifyMutation.mutate(agent.agentId)}
                  >
                    {agent.notifySubscribed ? '예약 완료' : '알림 받기'}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* ── 사용 가이드 모달 ──────────────────────────────────────────── */}
      {guideHtml !== null && (
        <GuideModal html={guideHtml} onClose={() => setGuideHtml(null)} />
      )}
    </div>
  );
}
